import { Text, TextStyle, IDestroyOptions } from "pixi.js";
import { GameFont } from "../../../../../common/GameFont";
import { ButtonWithScaleAnimation } from "../../../../../common/components/ButtonWithScaleAnimation";
import { IMarketplaceBuildingModel } from "../../models/building/IMarketplaceBuildingModel";
import { nameof } from "bindable-data";
import { StringFormatExtention } from "by/gamefactory/extensions/StringFormatExtention";
import { MarketplaceCard } from "../MarketplaceCard";
import { MarketplaceResourcePackage } from "../../../resources/MarketplaceResourcePackage";
import { MonField } from "../../../../../common/components/MonField";

enum ButtonStyle {
    EDIT,
    PURCHASE,
    PURCHASE_MINI
}

export class FullBuildingMainButton extends ButtonWithScaleAnimation {
    private static readonly EDIT_TEXT = "EDIT PRICE";
    private static readonly BUY_TEXT = "BUY FOR";

    private readonly marketplaceResources: MarketplaceResourcePackage;
    private readonly marketplaceBuildingModel: IMarketplaceBuildingModel;
    private readonly marketplaceCards: Array<MarketplaceCard>;

    private priceButtonText: Text;
    private usdPriceText: Text;
    private monPriceText: MonField;

    private style: ButtonStyle;

    public constructor(
        marketplaceResources: MarketplaceResourcePackage,
        marketplaceBuildingModel: IMarketplaceBuildingModel,
        marketplaceCards: Array<MarketplaceCard>
    ) {
        super(marketplaceResources.assets_views_marketplace_Button_green_long_png);
        this.marketplaceResources = marketplaceResources;
        this.marketplaceBuildingModel = marketplaceBuildingModel;
        this.marketplaceCards = marketplaceCards;
        this.build();
        this.showEditStyle();
        this.layout();
        this.marketplaceBuildingModel.propertyChanged.add(this.onMarketplaceBuildingModelPropertyChanged, this);
    }

    private async onMarketplaceBuildingModelPropertyChanged(model: IMarketplaceBuildingModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "currentBuilding"):
                const price = model.currentBuilding.value.price;
                this.usdPriceText.text = `$ ${StringFormatExtention.formatWithComma(price.usd ?? 0)}`;
                this.usdPriceText.visible = model.currentBuilding.value.price.usd != null;
                this.monPriceText.visible = !this.usdPriceText.visible;
                this.monPriceText.updatePrice(model.currentBuilding.value.price.mon ?? 0);
                this.layout();
                break;
        }
    }

    private build(): void {
        this.priceButtonText = new Text(FullBuildingMainButton.EDIT_TEXT, new EditButtonTextStyle());
        this.priceButtonText.position.set((this.defaultView.width - this.priceButtonText.width) * 0.5, 18);
        this.addChild(this.priceButtonText);

        this.monPriceText = new MonField(0, this.marketplaceResources.assets_views_marketplace_mon_icon_png, new EditButtonTextStyle(), 5);
        this.monPriceText.position.set((this.defaultView.width - this.monPriceText.width) * 0.5, this.priceButtonText.position.y + 8);
        this.monPriceText.visible = false;
        this.addChild(this.monPriceText);

        this.usdPriceText = new Text("$ 15", new EditButtonTextStyle());
        this.usdPriceText.position.set((this.defaultView.width - this.usdPriceText.width) * 0.5, this.priceButtonText.position.y + 8);
        this.usdPriceText.style.fontSize = 44;
        this.addChild(this.usdPriceText);
    }

    public showEditStyle(): void {
        this.changeStyle(ButtonStyle.EDIT);
    }

    public showPurchaseStyle(): void {
        this.changeStyle(ButtonStyle.PURCHASE);
    }

    public showPurchaseMiniStyle(): void {
        this.changeStyle(ButtonStyle.PURCHASE_MINI);
    }

    private changeStyle(style: ButtonStyle): void {
        if (this.style == style) {
            return;
        }

        switch (style) {
            case ButtonStyle.EDIT:
                this.priceButtonText.text = FullBuildingMainButton.EDIT_TEXT;
                this.defaultView.width = 297;
                this.defaultView.height = 135;
                break;
            case ButtonStyle.PURCHASE:
                this.priceButtonText.text = FullBuildingMainButton.BUY_TEXT;
                this.defaultView.scale.set(1);
                break;
            case ButtonStyle.PURCHASE_MINI:
                this.priceButtonText.text = FullBuildingMainButton.BUY_TEXT;
                this.defaultView.width = 297;
                this.defaultView.height = 135;
                break;
        }
        this.layout();
        this.style = style;
    }

    private layout(): void {
        this.priceButtonText.position.set(
            (this.defaultView.width - this.priceButtonText.width) * 0.5,
            (this.defaultView.height - this.priceButtonText.height - this.usdPriceText.height) * 0.5
        );
        this.monPriceText.position.set((this.defaultView.width - this.monPriceText.width) * 0.5, this.priceButtonText.position.y + this.priceButtonText.height);
        this.usdPriceText.position.set((this.defaultView.width - this.usdPriceText.width) * 0.5, this.priceButtonText.position.y + this.priceButtonText.height);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.marketplaceBuildingModel.propertyChanged.remove(this.onMarketplaceBuildingModelPropertyChanged, this);
        super.destroy(options);
    }
}

class EditButtonTextStyle extends TextStyle {
    public constructor() {
        super({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 52,
            dropShadow: true,
            dropShadowAlpha: 0.3,
            dropShadowColor: 0x0d0606,
            dropShadowAngle: 0.7,
            dropShadowBlur: 5.3,
            dropShadowDistance: 6.5
        });
    }
}
